import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, Calendar, Mail, ChevronDown, Check } from 'lucide-react';
import Footer from './Footer';

type FormType = 'Custom Data' | 'License Dataset' | 'Vendor Application' | 'General';

const SUBJECT_MAP: Record<FormType, string> = {
  'Custom Data': 'Custom Data Enquiry',
  'License Dataset': 'License Dataset Enquiry',
  'Vendor Application': 'Vendor Application',
  'General': 'General Enquiry',
};

export default function ContactScoping() {
  const [activeTab, setActiveTab] = useState<FormType>('Custom Data');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const renderFormFields = () => {
    switch (activeTab) {
      case 'Vendor Application':
        return (
          <>
            <div className="grid grid-cols-2 gap-4 md:gap-[clamp(22px,1.85vw,32px)]">
              <div className="flex flex-col relative group">
                <label className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] uppercase tracking-widest text-gray-500 mb-2 md:mb-[clamp(5px,0.46vw,8px)] transition-colors group-focus-within:text-[#56CFFC]">Name</label>
                <input name="Name" type="text" required className="bg-transparent border-b border-white/10 pb-2 text-white focus:outline-none focus:border-[#56CFFC] transition-colors" placeholder="Jane Doe" />
              </div>
              <div className="flex flex-col relative group">
                <label className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] uppercase tracking-widest text-gray-500 mb-2 md:mb-[clamp(5px,0.46vw,8px)] transition-colors group-focus-within:text-[#56CFFC]">Email</label>
                <input name="Email" type="email" required className="bg-transparent border-b border-white/10 pb-2 text-white focus:outline-none focus:border-[#56CFFC] transition-colors" placeholder="jane@example.com" />
              </div>
            </div>
            <div className="flex flex-col relative group">
              <label className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] uppercase tracking-widest text-gray-500 mb-2 md:mb-[clamp(5px,0.46vw,8px)] transition-colors group-focus-within:text-[#56CFFC]">Studio Name</label>
              <input name="Studio Name" type="text" required className="bg-transparent border-b border-white/10 pb-2 text-white focus:outline-none focus:border-[#56CFFC] transition-colors" placeholder="Acme Studios" />
            </div>
            <div className="flex flex-col relative group">
              <label className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] uppercase tracking-widest text-gray-500 mb-2 md:mb-[clamp(5px,0.46vw,8px)] transition-colors group-focus-within:text-[#56CFFC]">Acoustic Specs</label>
              <textarea name="Acoustic Specs" rows={2} className="bg-transparent border-b border-white/10 pb-2 text-white focus:outline-none focus:border-[#56CFFC] transition-colors resize-none" placeholder="Describe your recording environment, equipment, and noise floor..." />
            </div>
          </>
        );
      case 'Custom Data':
      case 'License Dataset':
      case 'General':
      default:
        return (
          <>
            <div className="grid grid-cols-2 gap-4 md:gap-[clamp(22px,1.85vw,32px)]">
              <div className="flex flex-col relative group">
                <label className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] uppercase tracking-widest text-gray-500 mb-2 md:mb-[clamp(5px,0.46vw,8px)] transition-colors group-focus-within:text-[#56CFFC]">Name</label>
                <input name="Name" type="text" required className="bg-transparent border-b border-white/10 pb-2 text-white focus:outline-none focus:border-[#56CFFC] transition-colors" placeholder="Jane Doe" />
              </div>
              <div className="flex flex-col relative group">
                <label className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] uppercase tracking-widest text-gray-500 mb-2 md:mb-[clamp(5px,0.46vw,8px)] transition-colors group-focus-within:text-[#56CFFC]">Email</label>
                <input name="Email" type="email" required className="bg-transparent border-b border-white/10 pb-2 text-white focus:outline-none focus:border-[#56CFFC] transition-colors" placeholder="jane@example.com" />
              </div>
            </div>
            <div className="flex flex-col relative group">
              <label className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] uppercase tracking-widest text-gray-500 mb-2 md:mb-[clamp(5px,0.46vw,8px)] transition-colors group-focus-within:text-[#56CFFC]">Company</label>
              <input name="Company" type="text" className="bg-transparent border-b border-white/10 pb-2 text-white focus:outline-none focus:border-[#56CFFC] transition-colors" placeholder="Acme AI" />
            </div>
            <div className="flex flex-col relative group">
              <label className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] uppercase tracking-widest text-gray-500 mb-2 md:mb-[clamp(5px,0.46vw,8px)] transition-colors group-focus-within:text-[#56CFFC]">Model Requirements</label>
              <textarea name="Requirements" rows={2} className="bg-transparent border-b border-white/10 pb-2 text-white focus:outline-none focus:border-[#56CFFC] transition-colors resize-none" placeholder="Describe your use case, languages, and volume needed..." />
            </div>
          </>
        );
    }
  };

  const getSubmitText = () => {
    switch (activeTab) {
      case 'Vendor Application': return 'Submit Studio Profile';
      case 'Custom Data': return 'Request Data Scoping';
      case 'License Dataset': return 'Request Dataset License';
      case 'General': return 'Send Message';
    }
  };

  return (
    <section id="contact" className="w-full min-h-screen flex flex-col relative z-20 shrink-0">
      <div className="flex-1 flex items-center justify-center pt-16 md:pt-[clamp(64px,5.5vh,96px)] pb-2 md:pb-[clamp(8px,1vh,24px)]">
      <div className="w-full max-w-[var(--max-content)] mx-auto px-[var(--px-container)] flex flex-col md:flex-row gap-3 md:gap-[var(--gap-large)] items-center">

        {/* ── Desktop Left Column ── */}
        <div className="hidden md:flex w-[40%] flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-[clamp(22px,1.85vw,32px)]"
          >
            <div className="inline-flex items-center gap-[clamp(4px,0.46vw,8px)] px-[clamp(16px,1.39vw,24px)] py-[clamp(7px,0.58vw,10px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
              Contact Us
            </div>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.05 }}
            viewport={{ once: true }}
            className="text-[length:var(--heading-usp)] font-black tracking-tighter leading-[0.9] text-white mb-[clamp(16px,1.39vw,24px)]"
          >
            LET'S BUILD<br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">YOUR DATASET.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-white/90 text-[length:var(--text-body)] leading-relaxed mb-[clamp(32px,2.78vw,48px)]"
          >
            Whether you need a bespoke collection built from scratch or instant access to our production datasets, our team is ready to scope your exact requirements. Tell us your use case, your languages, and your volume — we'll take it from there.
          </motion.p>


          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="flex flex-col gap-[clamp(8px,0.69vw,12px)]"
          >
            <div className="flex flex-col gap-1">
              <a href="mailto:enterprise@voqals.com" className="flex items-center gap-[clamp(8px,0.69vw,12px)] text-white hover:text-[#56CFFC] transition-colors text-[clamp(9px,0.81vw,14px)] w-fit">
                <Mail className="w-[clamp(9px,0.81vw,14px)] h-[clamp(9px,0.81vw,14px)]" />
                enterprise@voqals.com
              </a>
              <p className="text-[clamp(9px,0.69vw,12px)] text-white/50 pl-[clamp(17px,1.5vw,26px)]">Datasets & custom data collection enquiries</p>
            </div>
            <div className="flex flex-col gap-1">
              <a href="mailto:partners@voqals.com" className="flex items-center gap-[clamp(8px,0.69vw,12px)] text-white hover:text-[#56CFFC] transition-colors text-[clamp(9px,0.81vw,14px)] w-fit">
                <Mail className="w-[clamp(9px,0.81vw,14px)] h-[clamp(9px,0.81vw,14px)]" />
                partners@voqals.com
              </a>
              <p className="text-[clamp(9px,0.69vw,12px)] text-white/50 pl-[clamp(17px,1.5vw,26px)]">Talents, studios & vendor enquiries</p>
            </div>
          </motion.div>
        </div>

        {/* ── Mobile: Heading + subtitle ── */}
        <div className="md:hidden w-full flex flex-col items-center text-center">
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
              Contact Us
            </div>
          </div>
          <h2 className="text-[clamp(38px,12.8vw,52px)] font-black tracking-tighter leading-[0.9] text-white mb-4">
            LET'S BUILD<br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">YOUR DATASET.</span>
          </h2>
        </div>

        {/* ── Form Card (both mobile & desktop) ── */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="w-full md:w-[60%]"
        >
          <div
            className="gradient-border rounded-2xl md:rounded-[clamp(20px,1.85vw,32px)] backdrop-blur-xl shadow-2xl p-4 md:p-[clamp(27px,2.31vw,40px)] relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' }}
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#1B36FF]/8 blur-[100px] rounded-full pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#56CFFC]/8 blur-[100px] rounded-full pointer-events-none" />

            <div className="relative z-10">
              {/* Mobile: Dropdown */}
              <div className="md:hidden mb-4 relative">
                <label htmlFor="inquiry-type" className="text-[9px] uppercase tracking-widest text-gray-500 mb-1.5 block">Inquiry Type</label>
                <div className="relative">
                  <select
                    id="inquiry-type"
                    value={activeTab}
                    onChange={(e) => setActiveTab(e.target.value as FormType)}
                    className="w-full appearance-none bg-white/[0.03] border border-white/[0.08] rounded-xl py-2.5 px-4 pr-10 text-white text-sm focus:outline-none focus:border-[#56CFFC]/50 transition-colors"
                  >
                    <option value="Custom Data" className="bg-[#0a0a14]">Custom Data</option>
                    <option value="License Dataset" className="bg-[#0a0a14]">License Dataset</option>
                    <option value="Vendor Application" className="bg-[#0a0a14]">Vendor Application</option>
                    <option value="General" className="bg-[#0a0a14]">General</option>
                  </select>
                  <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                </div>
              </div>

              {/* Desktop: Pills */}
              <div className="hidden md:flex flex-wrap gap-[clamp(5px,0.46vw,8px)] mb-[clamp(27px,2.31vw,40px)]">
                {(['Custom Data', 'License Dataset', 'Vendor Application', 'General'] as FormType[]).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-[clamp(11px,0.93vw,16px)] py-[clamp(5px,0.46vw,8px)] rounded-full text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.2em] uppercase transition-all duration-300 border ${
                      activeTab === tab
                        ? 'bg-[#56CFFC]/10 border-[#56CFFC]/50 text-[#56CFFC] shadow-[0_0_15px_rgba(86,207,252,0.2)]'
                        : 'bg-transparent border-white/10 text-gray-500 hover:text-white hover:border-white/30'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Form */}
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-8 md:py-12 text-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#56CFFC]/20 to-[#1B36FF]/20 border border-[#56CFFC]/30 flex items-center justify-center mb-4">
                    <Check size={22} className="text-[#56CFFC]" />
                  </div>
                  <h3 className="text-white font-black text-lg md:text-xl tracking-tight uppercase mb-2">Submitted</h3>
                  <p className="text-gray-400 text-xs md:text-sm leading-relaxed max-w-xs">
                    Thank you for reaching out. Our team will review your enquiry and get back to you within 24 hours.
                  </p>
                  <button type="button" onClick={() => { setSubmitted(false); formRef.current?.reset(); }} className="mt-6 px-6 py-2 rounded-full border border-white/10 text-white text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-white/5 transition-colors">
                    Submit Another
                  </button>
                </div>
              ) : (
              <form
                ref={formRef}
                className="flex flex-col gap-4 md:gap-[clamp(22px,1.85vw,32px)]"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (submitting) return;
                  setSubmitting(true);
                  try {
                    const fd = new FormData(formRef.current!);
                    const fields: Record<string, string> = {};
                    fd.forEach((v, k) => { fields[k] = v as string; });
                    const payload = {
                      access_key: 'd3cbc6a2-6428-46cc-83a8-ee15a7f434ae',
                      subject: SUBJECT_MAP[activeTab] + (fields.Name ? ` — ${fields.Name}` : ''),
                      from_name: 'Voqals Contact Form',
                      replyto: fields.Email || '',
                      'Inquiry Type': activeTab,
                      ...fields,
                    };
                    const res = await fetch('https://api.web3forms.com/submit', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                      body: JSON.stringify(payload),
                    });
                    const data = await res.json();
                    console.log('Contact form response:', data);
                    setSubmitted(true);
                    window.gtag?.('event', 'conversion', { send_to: 'AW-18066710209/65G5CK-OyJYcEMG98KZD', value: 1.0, currency: 'INR' });
                  } catch (err) {
                    console.error('Contact form error:', err);
                    setSubmitted(true);
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col gap-4 md:gap-[clamp(22px,1.85vw,32px)]"
                  >
                    {renderFormFields()}
                  </motion.div>
                </AnimatePresence>

                <button disabled={submitting} className="group relative w-full mt-1 md:mt-[clamp(11px,0.93vw,16px)] px-8 py-3 md:py-[clamp(11px,0.93vw,16px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full text-xs md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(86,207,252,0.4)] disabled:opacity-50 flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {submitting ? 'Submitting...' : getSubmitText()}
                  </span>
                </button>
              </form>
              )}
            </div>
          </div>
        </motion.div>

        {/* Mobile: "or write to us" + email links */}
        <div className="md:hidden flex flex-col items-center gap-2 mt-1">
          <div className="flex items-center gap-3 w-full">
            <div className="h-[1px] flex-1 bg-white/10"></div>
            <span className="text-[9px] uppercase tracking-widest text-gray-500">OR WRITE TO US</span>
            <div className="h-[1px] flex-1 bg-white/10"></div>
          </div>
          <div className="flex flex-col items-center gap-1.5">
            <div className="flex flex-col items-center gap-0.5">
              <a href="mailto:enterprise@voqals.com" className="flex items-center gap-2 text-white hover:text-[#56CFFC] transition-colors text-sm font-medium">
                <Mail size={14} />
                enterprise@voqals.com
              </a>
              <p className="text-[10px] text-white/50">Datasets & custom data collection</p>
            </div>
            <div className="flex flex-col items-center gap-0.5">
              <a href="mailto:partners@voqals.com" className="flex items-center gap-2 text-white hover:text-[#56CFFC] transition-colors text-sm font-medium">
                <Mail size={14} />
                partners@voqals.com
              </a>
              <p className="text-[10px] text-white/50">Talents, studios & vendors</p>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Footer — shared 4-column footer with legal overlay state */}
      <Footer />
    </section>
  );
}
